import React, { useState, useEffect } from 'react'
import { X, Shirt, RotateCcw, Trash2, Check } from 'lucide-react'
import clothingService from '../services/clothingService.js'
import playCreateStorageService from '../services/playCreateStorageService.js'
import toast from 'react-hot-toast'

/**
 * 服装衣柜组件 - 展示和切换角色的所有服装
 */
export const ClothingWardrobe = ({ character, onClose, onClothingChanged, isEmbedded = false }) => {
  const [clothingList, setClothingList] = useState([])
  const [currentClothingId, setCurrentClothingId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeClothingId, setActiveClothingId] = useState(null)
  const [currentCharacter, setCurrentCharacter] = useState(character)

  // 加载服装列表和最新的角色数据
  useEffect(() => {
    loadClothingList()
    loadLatestCharacter()
  }, [character])

  // 加载最新的角色数据
  const loadLatestCharacter = async () => {
    try {
      const latestCharacter = await playCreateStorageService.getCharacterProfile(character.id)
      if (latestCharacter) {
        setCurrentCharacter(latestCharacter)
        console.log('📸 加载最新角色数据:', {
          name: latestCharacter.name,
          hasOriginalAvatar: !!latestCharacter.originalAvatar,
          hasAvatar: !!latestCharacter.avatar
        })
      }
    } catch (error) {
      console.error('❌ 加载角色数据失败:', error)
    }
  }

  const loadClothingList = async () => {
    try {
      setIsLoading(true)
      
      // 获取角色所有服装
      const list = await clothingService.getCharacterClothing(character.id)
      setClothingList(list)
      
      // 获取当前穿着的服装
      const state = await playCreateStorageService.getCharacterState(character.id)
      setCurrentClothingId(state?.currentClothingId || null)
      setActiveClothingId(state?.currentClothingId || null)
      
      console.log('📦 加载服装列表:', {
        总数: list.length,
        当前服装ID: state?.currentClothingId
      })
    } catch (error) {
      console.error('❌ 加载服装列表失败:', error)
      toast.error('加载服装列表失败')
    } finally {
      setIsLoading(false)
    }
  }

  // 切换到指定服装
  const handleSelectClothing = async (clothing) => {
    try {
      toast.loading('正在切换服装...', { id: 'switching-clothing' })
      
      // 设置当前服装
      await clothingService.setCurrentClothing(character.id, clothing.id)
      
      setCurrentClothingId(clothing.id)
      setActiveClothingId(clothing.id)
      
      toast.success(`已换上：${clothing.name}`, { id: 'switching-clothing' })
      
      // 通知父组件服装已更改
      if (onClothingChanged) {
        onClothingChanged(clothing)
      }
    } catch (error) {
      console.error('❌ 切换服装失败:', error)
      toast.error('切换失败：' + error.message, { id: 'switching-clothing' })
    }
  }

  // 恢复默认服装
  const handleRestoreDefault = async () => {
    try {
      toast.loading('正在恢复默认服装...', { id: 'restore-clothing' })
      
      await clothingService.restoreDefaultClothing(character.id)
      
      setCurrentClothingId(null)
      setActiveClothingId(null)
      
      toast.success('已恢复默认服装', { id: 'restore-clothing' })
      
      // 通知父组件服装已更改
      if (onClothingChanged) {
        onClothingChanged(null)
      }
    } catch (error) {
      console.error('❌ 恢复默认服装失败:', error)
      toast.error('恢复失败：' + error.message, { id: 'restore-clothing' })
    }
  }

  // 删除服装
  const handleDeleteClothing = async (clothing, e) => {
    e.stopPropagation() // 防止触发选择事件
    
    if (!confirm(`确定要删除服装「${clothing.name}」吗？`)) {
      return
    }
    
    try {
      toast.loading('正在删除服装...', { id: 'delete-clothing' })
      
      await clothingService.deleteClothing(clothing.id)
      
      // 如果删除的是当前穿着的服装，恢复默认
      if (currentClothingId === clothing.id) {
        await handleRestoreDefault()
      }
      
      // 重新加载列表
      await loadClothingList()
      
      toast.success('服装已删除', { id: 'delete-clothing' })
    } catch (error) {
      console.error('❌ 删除服装失败:', error)
      toast.error('删除失败：' + error.message, { id: 'delete-clothing' })
    }
  }

  // 创建Blob URL用于显示图片
  const getImageUrl = (clothing) => {
    // 优先使用generatedImageUrl（直接URL，避免Blob URL失效问题）
    if (clothing.generatedImageUrl) {
      return clothing.generatedImageUrl
    }
    // 备用：使用imageBlob
    if (clothing.imageBlob) {
      return clothingService.createBlobUrl(clothing.imageBlob)
    }
    // 最后：使用普通imageUrl
    return clothing.imageUrl
  }

  // 内容区域
  const content = (
    <div className={isEmbedded ? "h-full overflow-y-auto p-6" : "p-6"}>
      {isLoading ? (
        <div className="text-center py-12 text-dark-400">
          加载中...
        </div>
      ) : clothingList.length === 0 ? (
        <div className="text-center py-12">
          <Shirt size={48} className="mx-auto mb-4 text-dark-500" />
          <p className="text-dark-400 mb-2">还没有生成服装</p>
          <p className="text-sm text-dark-500">前往换装试衣间生成你的第一套服装吧！</p>
        </div>
      ) : (
        <>
          {/* 默认服装 */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">默认服装</h3>
            <div
              onClick={handleRestoreDefault}
              className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                currentClothingId === null
                  ? 'border-primary bg-primary/10'
                  : 'border-dark-600 hover:border-dark-500'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-dark-700 flex-shrink-0">
                  {currentCharacter.originalAvatar || currentCharacter.avatar ? (
                    <img
                      src={currentCharacter.originalAvatar || currentCharacter.avatar}
                      alt="默认立绘"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Shirt size={32} className="text-dark-500" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-white font-medium">原始立绘</h4>
                    {currentClothingId === null && (
                      <span className="px-2 py-0.5 bg-primary text-white text-xs rounded">
                        当前穿着
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-dark-400">
                    角色的初始服装
                  </p>
                </div>
                {currentClothingId === null && (
                  <Check size={24} className="text-primary" />
                )}
              </div>
            </div>
          </div>

          {/* 生成的服装列表 */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white">
                生成的服装
              </h3>
              <button
                onClick={handleRestoreDefault}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
              >
                <RotateCcw size={16} />
                恢复默认
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clothingList.map((clothing) => {
                const isActive = currentClothingId === clothing.id
                const imageUrl = getImageUrl(clothing)
                
                return (
                  <div
                    key={clothing.id}
                    onClick={() => handleSelectClothing(clothing)}
                    className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all group ${
                      isActive
                        ? 'border-primary shadow-lg shadow-primary/20'
                        : 'border-dark-600 hover:border-dark-500'
                    }`}
                  >
                    {/* 服装图片 */}
                    <div className="aspect-square bg-dark-700 relative">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={clothing.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Shirt size={48} className="text-dark-500" />
                        </div>
                      )}
                      
                      {/* 当前穿着标记 */}
                      {isActive && (
                        <div className="absolute top-2 right-2">
                          <div className="px-2 py-1 bg-primary text-white text-xs rounded-lg flex items-center gap-1 shadow-lg">
                            <Check size={14} />
                            当前穿着
                          </div>
                        </div>
                      )}
                      
                      {/* 删除按钮 */}
                      <button
                        onClick={(e) => handleDeleteClothing(clothing, e)}
                        className="absolute top-2 left-2 p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    
                    {/* 服装信息 */}
                    <div className="p-3 bg-dark-700">
                      <h4 className="text-white font-medium truncate mb-1">
                        {clothing.name}
                      </h4>
                      <p className="text-xs text-dark-400 line-clamp-2">
                        {clothing.description}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs px-2 py-0.5 bg-dark-600 text-dark-300 rounded">
                          {getCategoryName(clothing.category)}
                        </span>
                        <span className="text-xs text-dark-500">
                          {formatDate(clothing.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )

  // 如果是嵌入模式，直接返回内容
  if (isEmbedded) {
    return content
  }

  // 独立模式：包含弹窗容器和头部
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto border border-dark-600">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600 sticky top-0 bg-dark-800 z-10">
          <div className="flex items-center gap-3">
            <Shirt size={24} className="text-primary" />
            <div>
              <h2 className="text-2xl font-bold text-white">
                {character.name} 的衣柜
              </h2>
              <p className="text-sm text-dark-400">
                共 {clothingList.length} 套服装
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        {content}
      </div>
    </div>
  )
}

// 辅助函数：获取类别名称
function getCategoryName(category) {
  const categoryMap = {
    top: '上装',
    bottom: '下装',
    dress: '连衣裙',
    accessory: '配饰',
    full: '全身'
  }
  return categoryMap[category] || category
}

// 辅助函数：格式化日期
function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diff = now - date
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  
  if (days === 0) return '今天'
  if (days === 1) return '昨天'
  if (days < 7) return `${days}天前`
  
  return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
}

